class ConditionsButtons extends HTMLElement {
  #variantOptions;
  #itemsInStock;
  maxLeftInStock;
  maxLeftInStockEl;
  initialVariantId;

  constructor() {
    super();

    // Cache the main container
    this.productInfo = this.closest("product-info");
    this.#variantOptions = this.productInfo.querySelector(
      ".product-options-btns__container",
    );
    this.#itemsInStock = this.productInfo.querySelector(".items-in-stock");
    this.maxLeftInStockEl = this.productInfo.querySelector(".js-only-left");
    this.maxLeftInStock = Number(this.maxLeftInStockEl?.dataset.max);
    this.initialVariantId = new URLSearchParams(window.location.search).get(
      "variant",
    );

    this.filtersLogic();
    this.initializeWhenVariantPickerReady();

    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      this.updateLeftInStock();
    });
  }

  initializeWhenVariantPickerReady() {
    if (this.#variantOptions) {
      this.initializeSelection();
      return;
    }

    const observer = new MutationObserver(() => {
      this.#variantOptions = this.productInfo.querySelector(
        ".product-options-btns__container",
      );

      if (this.#variantOptions) {
        observer.disconnect();
        this.initializeSelection();
      }
    });

    observer.observe(this.productInfo, { childList: true, subtree: true });
  }

  // Kick off the right selection flow based on URL
  initializeSelection() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get("variant");
    const variantsParam = params.get("variants");

    if (window.location.href.includes("/pages/suite") && variantsParam) {
      this.handleSuiteVariants(variantsParam);
    } else if (!variantId) {
      this.selectDefaultOption();
    } else {
      const selectedVariant = this.#variantOptions.querySelector(
        `.product-variant[data-variant-id="${variantId}"]`,
      );
      if (selectedVariant) {
        this.filterVariants(selectedVariant.dataset.condition);
        this.updateVariantPickerVisibility(selectedVariant.dataset.condition);
        this.querySelectorAll(".js-cb-filter").forEach((filter) => {
          filter.classList.toggle(
            "ppf-checked",
            filter.dataset.value === selectedVariant.dataset.condition,
          );
        });
        this.rebaseVariantPicker();
      }
    }
  }

  handleSuiteVariants(variantsString) {
    const variants =
      this.closest("product-info").querySelectorAll(".product-variant");
    variants.forEach((variant) => {
      if (variantsString.includes(variant.dataset.variantId)) {
        this.applyConditionFilter(variant.dataset.condition);
      }
    });
    this.rebaseVariantPicker();
  }

  // Click the filter whose data-value matches `conditionValue`
  applyConditionFilter(conditionValue) {
    this.querySelectorAll(".js-cb-filter").forEach((filter) => {
      if (filter.dataset.value === conditionValue) {
        filter.click();
      }
    });
  }

  selectDefaultOption() {
    const availableFilters = Array.from(
      this.querySelectorAll(
        `.js-cb-filter:not(:has(span[data-prod-option-count="0"]))`,
      ),
    );

    if (availableFilters.length > 0) {
      const firstAvailable = availableFilters[0];
      firstAvailable.click();
      this.updateVariantPickerVisibility(firstAvailable.dataset.value);
      this.#itemsInStock.textContent =
        this.#itemsInStock.dataset.productQty + " In-Stock";
      this.#itemsInStock
        .closest(".select-from-qty--box")
        ?.classList.remove("hidden");
    } else {
      this.#itemsInStock
        .closest(".select-from-qty--box")
        ?.classList.add("hidden");
      this.querySelectorAll(".js-cb-filter").forEach((el) =>
        el.classList.remove("ppf-checked"),
      );
    }
    this.rebaseVariantPicker();
  }

  isLotCondition(condition) {
    return String(condition || "").trim().toLowerCase() === "lot";
  }

  updateVariantPickerVisibility(condition) {
    this.closest(".price-and-filter")?.classList.toggle(
      "price-and-filter--hide-variant-picker",
      this.isLotCondition(condition),
    );
  }

  updateLeftInStock() {
    setTimeout(() => {
      //amt means amount (stock quantity)
      const amt = Number(
        this.querySelector(".js-cb-filter.ppf-checked [data-prod-option-count]")
          .dataset.prodOptionCount,
      );

      const infoBox = this.closest("product-info");
      if (amt <= this.maxLeftInStock) {
        infoBox.querySelector(".amount-left").innerHTML = amt;
        infoBox.classList.add("low-stock-info-active");
      } else {
        infoBox.classList.remove("low-stock-info-active");
      }
    }, 50);
  }

  selectFirstAvailableOption() {
    const productInfo = this.closest("product-info");
    const variants = Array.from(
      productInfo.querySelectorAll(
        ".product-options-btns__container .product-variant",
      ),
    );

    const isSelectable = (el) =>
      !el.classList.contains("hide-variant-option") &&
      !el.classList.contains("hide-variant-option--force") &&
      Number(el.dataset.invertory) > 0;

    const preferred = this.initialVariantId
      ? variants.find(
          (el) =>
            el.dataset.variantId === this.initialVariantId && isSelectable(el),
        )
      : null;

    const target = preferred || variants.find(isSelectable);

    if (target && !window.location.search.includes("old_key=")) {
      const input = target.querySelector("input");
      input.click();
      input.dispatchEvent(new Event("change", { bubbles: true }));
      this.applyConditionFilter(target.dataset.condition);
    }
  }

  showOutOfStockInfo(parent) {
    parent.querySelector(".popover-message").classList.add("active");
    setTimeout(() => {
      parent.querySelector(".popover-message").classList.remove("active");
    }, 5000);
  }

  rebaseVariantPicker() {
    const checkedFilter = this.querySelector(".ppf-checked");
    const picker = this.productInfo.querySelector("variant-selects");
    const target =
      checkedFilter?.querySelector(".variant-selects--container") ||
      this.productInfo.querySelector(
        ".variant-selects--container.product-variants-container",
      );

    if (!picker || !target || target.contains(picker)) return;

    target.replaceChildren(picker);
  }

  filterVariants(condition) {
    if (!this.#variantOptions) return;

    this.#variantOptions
      .querySelectorAll(".product-variant")
      .forEach((opt) => {
        opt.classList.toggle(
          "hide-variant-option",
          opt.dataset.condition !== condition,
        );
      });
  }

  filtersLogic() {
    this.querySelectorAll(".js-cb-filter").forEach((filter) => {
      filter.addEventListener("click", (e) => {
        if (filter.classList.contains("ppf-checked")) return;
        const count = filter.querySelector("[data-prod-option-count]").dataset
          .prodOptionCount;
        if (count === "0") {
          this.showOutOfStockInfo(filter);
          return;
        }

        // Toggle checked class
        this.querySelectorAll(".js-cb-filter").forEach((f) =>
          f.classList.remove("ppf-checked"),
        );
        filter.classList.add("ppf-checked");

        // Show/hide product variants
        this.filterVariants(filter.dataset.value);
        this.updateVariantPickerVisibility(filter.dataset.value);
        this.rebaseVariantPicker();

        // Auto-select first available after filtering
        this.selectFirstAvailableOption();

        // Dispatch custom event
        window.dispatchEvent(
          new CustomEvent("filter::changed", {
            detail: filter.dataset.value,
          }),
        );

        // Low-stock indicator
        const amt = Number(count);
        const infoBox = this.closest("product-info");
        if (amt <= this.maxLeftInStock) {
          infoBox.classList.add("low-stock-info-active");
          this.maxLeftInStockEl.querySelector(".amount-left").innerHTML = amt;
        } else {
          infoBox.classList.remove("low-stock-info-active");
        }
      });

      filter.addEventListener("mouseover", () => {
        if (
          filter.querySelector("[data-prod-option-count]").dataset
            .prodOptionCount === "0"
        ) {
          filter.querySelector(".popover-message").classList.add("active");
        }
      });

      filter.addEventListener("mouseleave", () => {
        if (
          filter.querySelector("[data-prod-option-count]").dataset
            .prodOptionCount === "0"
        ) {
          filter.querySelector(".popover-message").classList.remove("active");
        }
      });
    });
  }
}

customElements.define("conditions-buttons", ConditionsButtons);
